// src/Lib/utils.js
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"

// Existing utility function
export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

// HR Constants
export const EMPLOYEE_TYPES = {
  SAUDI: 'Saudi',
  NON_SAUDI: 'Non-Saudi'
}

export const EMPLOYEE_STATUS = {
  ACTIVE: 'Active',
  ON_LEAVE: 'On Leave',
  TERMINATED: 'Terminated',
  SUSPENDED: 'Suspended'
}

export const PERMIT_COSTS = {
  WORK_PERMIT: 9800,
  IQAMA: 650
}

export const LEAVE_TYPES = {
  ANNUAL: 'Annual',
  SICK: 'Sick',
  EMERGENCY: 'Emergency',
  UNPAID: 'Unpaid'
}

// HR Utility Functions
export const calculateSalary = (basicSalary, employeeType) => {
  const housingAllowance = basicSalary * 0.25;
  const transportAllowance = basicSalary * 0.25;
  const totalSalary = basicSalary + housingAllowance + transportAllowance;

  if (employeeType === EMPLOYEE_TYPES.NON_SAUDI) {
    return {
      basicSalary,
      housingAllowance,
      transportAllowance,
      totalSalary,
      workPermitCost: PERMIT_COSTS.WORK_PERMIT,
      iqamaCost: PERMIT_COSTS.IQAMA,
      totalCost: totalSalary + PERMIT_COSTS.WORK_PERMIT + PERMIT_COSTS.IQAMA,
      monthlyCost: totalSalary + (PERMIT_COSTS.WORK_PERMIT + PERMIT_COSTS.IQAMA) / 12
    }
  }

  return {
    basicSalary,
    housingAllowance,
    transportAllowance,
    totalSalary,
    totalCost: totalSalary,
    monthlyCost: totalSalary
  }
}

export const calculateLeaveBalance = (joiningDate, leaveHistory = []) => {
  const today = new Date();
  const joinDate = new Date(joiningDate);
  const monthsWorked = (today.getFullYear() - joinDate.getFullYear()) * 12 + 
                      (today.getMonth() - joinDate.getMonth());
  
  // Standard annual leave allowance (30 days per year)
  const annualAllowance = Math.floor((30 / 12) * monthsWorked);
  
  // Calculate used leave days
  const usedLeave = leaveHistory.reduce((total, leave) => total + leave.days, 0);
  
  return {
    total: annualAllowance,
    used: usedLeave,
    remaining: annualAllowance - usedLeave
  }
}

export const formatSalary = (amount) => {
  return new Intl.NumberFormat('en-SA', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export const calculateServiceYears = (joiningDate) => {
  const today = new Date();
  const joinDate = new Date(joiningDate);
  const years = today.getFullYear() - joinDate.getFullYear();
  const months = today.getMonth() - joinDate.getMonth();
  
  const totalYears = years + (months / 12);
  return Math.round(totalYears * 10) / 10; // Round to 1 decimal place
}

export const getEmployeeStatus = (status) => {
  const statusConfig = {
    [EMPLOYEE_STATUS.ACTIVE]: {
      color: 'bg-green-100 text-green-800',
      icon: 'CheckCircle'
    },
    [EMPLOYEE_STATUS.ON_LEAVE]: {
      color: 'bg-yellow-100 text-yellow-800',
      icon: 'Clock'
    },
    [EMPLOYEE_STATUS.TERMINATED]: {
      color: 'bg-red-100 text-red-800',
      icon: 'XCircle'
    },
    [EMPLOYEE_STATUS.SUSPENDED]: {
      color: 'bg-gray-100 text-gray-800',
      icon: 'AlertCircle'
    }
  }

  return statusConfig[status] || statusConfig[EMPLOYEE_STATUS.ACTIVE];
}

// Date formatting utilities
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-SA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export const calculateAge = (dateOfBirth) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
}

// Validation utilities
export const validateNationalId = (id, type) => {
  if (type === EMPLOYEE_TYPES.SAUDI) {
    return /^[12]\d{9}$/.test(id); // Saudi National ID: 1 or 2 followed by 9 digits
  }
  return /^\d{10}$/.test(id); // Iqama: 10 digits
}

export const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export const validatePhone = (phone) => {
  return /^(\+966|0)?5\d{8}$/.test(phone); // Saudi phone format
}